/**
 * Narrow a filter dropdown to values that still exist after the other filters.
 * The dropdown's own selection is not used as a constraint, so it can be changed.
 *
 * @param {Array<{ title?: string, value?: unknown }>} options
 * @param {Array<unknown>} allowedValues
 * @param {{
 *   constrain?: boolean,
 *   selected?: unknown,
 *   optionKey?: (option: { value?: unknown }) => unknown,
 *   allowedKey?: (value: unknown) => unknown,
 *   normalize?: (value: unknown) => string,
 *   extraOption?: (value: unknown) => { title: string, value: unknown }|null,
 *   alwaysKeep?: (option: { value?: unknown }) => boolean,
 * }} [config]
 * @returns {Array<{ title?: string, value?: unknown }>}
 */
export function restrictFilterOptions(options, allowedValues, config = {}) {
    const {
        constrain = true,
        selected = null,
        optionKey = (option) => option?.value,
        allowedKey = (value) => value,
        normalize = (value) => String(value ?? '').trim().toLocaleLowerCase('ro'),
        extraOption = (value) => ({
            title: String(value ?? '').trim(),
            value,
        }),
        alwaysKeep = () => false,
    } = config;

    const source = Array.isArray(options) ? options : [];
    if (!constrain) {
        return source;
    }

    const allowed = new Set();
    for (const value of allowedValues || []) {
        const key = normalize(allowedKey(value));
        if (key) {
            allowed.add(key);
        }
    }

    const next = [];
    const seen = new Set();
    const push = (option) => {
        const key = normalize(optionKey(option));
        if (!key || seen.has(key)) {
            return;
        }
        seen.add(key);
        next.push(option);
    };

    for (const option of source) {
        if (alwaysKeep(option)) {
            next.push(option);
            continue;
        }
        const key = normalize(optionKey(option));
        if (key && allowed.has(key)) {
            push(option);
        }
    }

    for (const value of allowedValues || []) {
        const key = normalize(allowedKey(value));
        if (!key || seen.has(key)) {
            continue;
        }
        const option = extraOption(value);
        if (option) {
            push(option);
        }
    }

    if (selected != null && selected !== '') {
        const selectedKey = normalize(optionKey({ value: selected }));
        if (selectedKey && !seen.has(selectedKey)) {
            const existing = source.find(
                (option) => normalize(optionKey(option)) === selectedKey,
            );
            const kept = existing || extraOption(selected);
            if (kept) {
                next.unshift(kept);
            }
        }
    }

    return next;
}
